<i18n src="./resources/locales.json"></i18n>

<template>
  <v-dialog v-model="dialog" v-resize="onResize" width="480" max-width="480" @click:outside="cancelAssignment">
    <v-card color="surface lighten-1">
      <v-card-title primary-title>
        {{ title }}

        <v-btn class="close-btn" x-small icon @click="cancelAssignment">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- eslint-disable-next-line vue/no-v-html -->
      <v-card-subtitle class="pt-2 pb-0 px-6" v-html="description"></v-card-subtitle>

      <v-row align="center" class="py-2 px-6" no-gutters>
        <EcsSearchInput ref="searchBar" :placeholder="searchPlaceholder" @search-input-changed="handleSearch" />
      </v-row>

      <v-card-text class="pa-0">
        <v-list class="pa-0 surface">
          <v-virtual-scroll
            ref="agentsContainer"
            class="scroller custom-scrollbar"
            :items="filteredAgents"
            :item-height="48"
            :bench="1"
            :height="availableTotalHeight"
          >
            <template v-slot="{ item }">
              <v-list-item class="px-6">
                <div class="mr-2">
                  <EcsAvatar :contact-id="item.id" :alt="item.name" :size="40" no-animation />
                </div>

                <v-list-item-content>
                  <v-list-item-title v-text="item.name"></v-list-item-title>
                </v-list-item-content>

                <v-list-item-action class="flex-row">
                  <div v-if="item.expirationTime" class="expiration-time-label">{{ fromNow(item.expirationTime) }}</div>
                  <v-checkbox v-model="item.selected"></v-checkbox>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-virtual-scroll>
        </v-list>
      </v-card-text>

      <v-card-actions class="px-6">
        <v-spacer></v-spacer>
        <v-btn text @click="cancelAssignment">
          <span v-once>{{ $t('options.cancel') }}</span>
        </v-btn>
        <div class="primary selection-btn">
          <v-btn depressed class="action-btn primary" :disabled="!selectedAgents.length" @click="handleSaveChanges">
            {{ saveButtonCounterText }}
          </v-btn>
          <v-divider vertical inset dark class="selection-btn-divider"></v-divider>
          <v-menu max-height="288" content-class="custom-scrollbar">
            <template v-slot:activator="{ on, attrs }">
              <v-btn depressed class="menu-btn primary" v-bind="attrs" v-on="on">
                {{ `${availableAssignmentDurations[selectedAssignmentDuration]} HR` }}
                <v-icon right>
                  mdi-menu-down
                </v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item-group v-model="selectedAssignmentDuration">
                <v-list-item v-for="(duration, index) in availableAssignmentDurations" :key="index">
                  <v-list-item-title>{{ duration }}</v-list-item-title>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-menu>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import { sortByName } from '@/utils';
import EcsAvatar from '@/components/ecs-base-interaction-components/ecs-avatar/EcsAvatar';
import EcsTimeFormatter from '@/mixins/ecs-time-formatter/EcsTimeFormatter';

export default {
  name: 'EcsAgentAssignmentDialog',

  components: { EcsAvatar },

  mixins: [EcsTimeFormatter],

  props: {
    agents: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    saveButtonText: {
      type: String,
      required: true
    },
    searchPlaceholder: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      items: [],
      availableTotalHeight: 0,
      availableAssignmentDurations: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      selectedAssignmentDuration: 5,
      dialog: false,
      searchInput: ''
    };
  },

  computed: {
    ...mapGetters('SETTINGS', ['applicationSettings']),

    filteredAgents() {
      return this.items.filter(this.includesSearchValue);
    },

    selectedAgents() {
      return this.items.filter(agent => agent.selected);
    },

    saveButtonCounterText() {
      return `${this.saveButtonText} (${this.selectedAgents.length})`;
    }
  },

  watch: {
    agents: {
      handler(value) {
        this.items = this.initAgents(value);
      },
      immediate: true
    }
  },

  created() {
    this.calculateHeight();
  },

  mounted() {
    if (this.data) {
      this.$refs.searchInput.searchText = this.data.searchText;
    }
  },

  methods: {
    /**
     * Function to evaluate if a contact matches with the search criteria.
     * @param contact The contact to be evaluted
     * @return includes - The boolean that indicates if the contact matches with the search criteria.
     */
    includesSearchValue(contact) {
      return contact ? new RegExp(this.searchInput, 'i').test(contact.name) : false;
    },

    /**
     * This method will set the input search value into a variable that will be used for filter criterias.
     * Also emmit the CRM trigger to retrieve contact based on the search value.
     * @param searchValue The search input value.
     */
    handleSearch(searchValue) {
      this.searchInput = searchValue;
    },

    open() {
      this.dialog = true;
    },

    /**
     * It calculates the available height
     */
    calculateHeight() {
      const dialogTitleHeight = 54;
      const dialogSubtitle = 48;
      const searchSectionHeight = 53;
      const actionsHeight = 52;

      this.availableTotalHeight =
        window.innerHeight * 0.9 - (dialogTitleHeight + dialogSubtitle + searchSectionHeight + actionsHeight);
    },

    handleSaveChanges() {
      this.$emit('save-changes', {
        agents: this.selectedAgents,
        duration: this.availableAssignmentDurations[this.selectedAssignmentDuration]
      });
      this.close();
    },

    cancelAssignment() {
      this.close();
    },

    close() {
      this.items = this.initAgents(this.agents);
      this.$refs.searchBar.clear();
      this.$refs.agentsContainer.$el.scrollTop = 0;
      this.dialog = false;
    },

    initAgents(value) {
      return value.map(agent => ({ ...agent, selected: false })).sort(sortByName);
    },

    onResize() {
      this.calculateHeight();
    }
  }
};
</script>

<style lang="scss" scoped>
.scroller {
  max-height: 800px;
}

.close-btn {
  position: absolute;
  right: 24px;
}

.selection-btn {
  height: 36px;
  width: fit-content;
  border-radius: 4px;
  display: inline-flex;
  margin-left: 16px;

  & >>> .v-btn:first-child {
    border-radius: 4px 0 0 4px;
  }

  & >>> .v-btn:last-of-type {
    border-radius: 0 4px 4px 0;
  }
}

.selection-btn-divider {
  border-color: rgba(255, 255, 255, 0.5) !important;
}

.action-btn {
  border-radius: 4px 0 0 4px;
}

.menu-btn {
  transition: all 250ms ease;
  border-radius: 0 4px 4px 0;

  &:focus >>> .v-icon {
    transform: rotate(180deg);
  }
}

.expiration-time-label {
  margin-right: 8px;
  line-height: 24px;
}
</style>
